import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, CheckCircle2, FileText, 
  Zap, Clock, Calendar,
  ChevronRight, Truck, Receipt, CreditCard, 
  Calculator, Share2, Eye, Send,
  Camera, Mic, Hash, MessageSquare, ClipboardCheck,
  Users, Image, BookOpen, Shield, SunMedium,
  CloudRain, HardHat, Wrench, AlertCircle, Trophy,
  AlertTriangle, CalendarDays, Notebook
} from 'lucide-react';
import RequestSetupModal from '../components/ui/request-setup-modal';
import ShareModal from '../components/ui/share-modal';

// Before/After Mockup Component for Site Updates
const BeforeAfterMockup = () => {
  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Desktop Layout */}
      <div className="hidden md:flex items-center justify-between gap-4">
        {/* Before - Scattered Updates */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex-1 bg-slate-100 rounded-2xl p-6 border-2 border-slate-200"
        >
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Before 2Tap...</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <MessageSquare className="w-8 h-8 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Scattered Texts</p>
                <p className="text-xs text-slate-500">Updates lost in group chats</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <Image className="w-8 h-8 text-amber-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Photos Everywhere</p>
                <p className="text-xs text-slate-500">Camera roll chaos</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <Clock className="w-8 h-8 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">No Timeline</p>
                <p className="text-xs text-slate-500">"When did that happen?"</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <BookOpen className="w-8 h-8 text-slate-400" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Empty Site Diary</p>
                <p className="text-xs text-slate-500">Nothing recorded</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Arrow */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-col items-center gap-2"
        >
          <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <p className="text-xs font-bold text-blue-500 text-center">2TAP<br/>CAPTURES</p>
        </motion.div>

        {/* After - 2Tap Output */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex-1 bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border-2 border-blue-200"
        >
          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-4">2Tap creates...</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <Notebook className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Site Diary Entry</p>
                <p className="text-xs text-blue-600">Timestamped & organized</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <Image className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Photos Attached</p>
                <p className="text-xs text-blue-600">Linked to the right job</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <CalendarDays className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Complete Timeline</p>
                <p className="text-xs text-blue-600">Full job history</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <Send className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Share with Builder</p>
                <p className="text-xs text-blue-600">Keep everyone updated</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden space-y-4">
        <div className="bg-slate-100 rounded-xl p-4 border border-slate-200">
          <p className="text-xs font-bold text-slate-500 mb-3">BEFORE</p>
          <div className="flex items-center gap-3">
            <MessageSquare className="w-10 h-10 text-red-500" />
            <div>
              <p className="text-sm font-semibold">Scattered updates</p>
              <p className="text-xs text-slate-500">Lost in chat threads</p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-200">
          <p className="text-xs font-bold text-blue-600 mb-3">AFTER</p>
          <div className="flex items-center gap-3">
            <Notebook className="w-10 h-10 text-blue-500" />
            <div>
              <p className="text-sm font-semibold">Organized site diary</p>
              <p className="text-xs text-blue-600">Full job timeline</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Example Site Update Output Component
const ExampleSiteUpdateOutput = () => {
  const [activeTab, setActiveTab] = useState('update');

  return (
    <div className="grid md:grid-cols-2 gap-8 items-start">
      {/* Left - Input */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="bg-slate-100 rounded-2xl p-6"
      >
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Mic className="w-4 h-4" />
          YOU TELL 2TAP
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Voice note transcription:</p>
              <p className="text-sm text-slate-700 italic">
                "End of day at the Smith renovation. Finished the first fix electrical today - 
                all power points and light switches roughed in. Had a delay this morning, 
                plumber was still finishing so couldn't start til 10. Weather's been good. 
                Tomorrow we're doing the switchboard upgrade."
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Camera className="w-4 h-4 text-slate-400" />
            <span>+ 4 progress photos attached</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Users className="w-4 h-4 text-slate-400" />
            <span>+ 2 workers on site today</span>
          </div>
        </div>
      </motion.div>

      {/* Right - Output with Tabs */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="bg-white rounded-2xl shadow-lg border border-blue-100 overflow-hidden"
      >
        {/* Tab Header */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          {[
            { id: 'update', label: 'Site Update', icon: Notebook },
            { id: 'conditions', label: 'Conditions', icon: SunMedium },
            { id: 'diary', label: 'Site Diary', icon: BookOpen }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-3 text-xs font-semibold transition-colors ${
                activeTab === tab.id 
                  ? 'text-blue-600 bg-white border-b-2 border-blue-500' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'update' && (
            <div className="space-y-3">
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5" />
                  UPDATE: SU-0156
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Job:</span> 
                    <span className="font-medium">Smith Renovation - Electrical</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Category:</span> 
                    <span className="font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Progress Update</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Date:</span> 
                    <span className="font-medium">8 Jul 2026, 4:45 PM</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Work Completed</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5" />
                    First fix electrical complete
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5" />
                    All power points roughed in
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5" />
                    Light switch locations marked
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  DELAY NOTED
                </p>
                <p className="text-xs text-amber-800">Started late (10am) - waiting on plumber to finish</p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Tomorrow's Plan</p>
                <p className="text-xs text-slate-600">Switchboard upgrade and meter box relocation</p>
              </div>
            </div>
          )}

          {activeTab === 'conditions' && (
            <div className="space-y-3">
              <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-100">
                <p className="text-xs font-bold text-cyan-700 mb-2 flex items-center gap-1.5">
                  <SunMedium className="w-3.5 h-3.5" />
                  SITE CONDITIONS
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                  <SunMedium className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-slate-800">Weather</p>
                  <p className="text-[10px] text-slate-500">Clear & sunny</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                  <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-slate-800">Workers</p>
                  <p className="text-[10px] text-slate-500">2 on site</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Hours Worked</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-600">Start Time</span>
                  <span className="text-xs font-semibold">10:00 AM</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-slate-600">End Time</span>
                  <span className="text-xs font-semibold">4:45 PM</span>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                  <span className="text-xs font-medium text-slate-700">Total Hours</span>
                  <span className="text-xs font-bold text-blue-600">6.75 hrs</span>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Other Trades on Site</p>
                <div className="flex items-center gap-2">
                  <span className="bg-slate-100 text-slate-600 text-[10px] font-medium px-2 py-1 rounded">Plumber (AM only)</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'diary' && (
            <div className="space-y-3">
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <p className="text-xs font-bold text-purple-700 mb-2 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5" />
                  SITE DIARY - SMITH RENOVATION
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Recent Entries</p>
                <div className="space-y-2">
                  {[
                    { date: '8 Jul', title: 'First fix electrical', status: 'Complete' },
                    { date: '7 Jul', title: 'Cable runs & conduit', status: 'Complete' },
                    { date: '5 Jul', title: 'Site setup & materials', status: 'Complete' },
                    { date: '4 Jul', title: 'Pre-start inspection', status: 'Complete' }
                  ].map((entry, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400 w-10">{entry.date}</span>
                        <span className="text-xs font-medium text-slate-700">{entry.title}</span>
                      </div>
                      <span className="text-[10px] text-green-600 font-medium">{entry.status}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Photos (4)</p>
                <div className="grid grid-cols-4 gap-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="aspect-square bg-slate-100 rounded flex items-center justify-center">
                      <Camera className="w-4 h-4 text-slate-400" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Export Options</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-blue-100 rounded-lg p-2 text-center cursor-pointer hover:bg-blue-200 transition-colors">
                    <FileText className="w-4 h-4 text-blue-600 mx-auto mb-1" />
                    <p className="text-[10px] font-semibold text-blue-700">PDF Report</p>
                  </div>
                  <div className="bg-green-100 rounded-lg p-2 text-center cursor-pointer hover:bg-green-200 transition-colors">
                    <Send className="w-4 h-4 text-green-600 mx-auto mb-1" />
                    <p className="text-[10px] font-semibold text-green-700">Email to Builder</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Next Action */}
        <div className="p-4 pt-0">
          <div className="bg-blue-500 rounded-lg p-3 text-center cursor-pointer hover:bg-blue-600 transition-colors">
            <p className="text-xs font-bold text-white flex items-center justify-center gap-2">
              Save to Site Diary
              <BookOpen className="w-4 h-4" />
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Workflow Step Component
const WorkflowStep = ({ icon: Icon, label, isActive, isCompleted }) => (
  <div className={`flex flex-col items-center gap-2 ${isActive ? 'opacity-100' : 'opacity-60'}`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
      isActive ? 'bg-blue-500 text-white' : 
      isCompleted ? 'bg-green-500 text-white' :
      'bg-slate-200 text-slate-500'
    }`}>
      <Icon className="w-6 h-6" />
    </div>
    <p className={`text-xs font-medium text-center max-w-[80px] ${isActive ? 'text-blue-600' : 'text-slate-600'}`}>
      {label}
    </p>
  </div>
);

// Main Page Component
const FeatureSiteUpdates = () => {
  const navigate = useNavigate();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Navigation links for related areas
  const relatedAreas = [
    { icon: Calculator, label: 'Scope & Quotes', href: '/features/scope-costing-quote', color: 'emerald' },
    { icon: ClipboardCheck, label: 'SWMS & Safety', href: '/features/swms-safety', color: 'blue' },
    { icon: Truck, label: 'Site Updates', href: '/features/site-updates', color: 'blue', active: true },
    { icon: AlertTriangle, label: 'Variations', href: '/features/variations', color: 'blue' },
    { icon: Receipt, label: 'Invoicing', href: '/features', color: 'orange' },
    { icon: CreditCard, label: 'Payments', href: '/features', color: 'orange' }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 bg-[#1F2A37] rounded-lg flex items-center justify-center">
              <span className="text-orange-500 font-bold text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>2</span>
            </div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>2TAP</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => navigate('/')} className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide">HOME</button>
            <button onClick={() => navigate('/features')} className="text-orange-500 text-sm font-medium tracking-wide">FEATURES</button>
            <button onClick={() => setShowShareModal(true)} className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide flex items-center gap-1.5">
              <Share2 className="w-4 h-4" />SHARE
            </button>
            <Button onClick={() => navigate('/login')} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-6 font-semibold tracking-wide shadow-[3px_3px_0px_rgba(0,0,0,0.2)]">LOG IN</Button>
          </div>

          <div className="md:hidden flex items-center gap-1">
            <button onClick={() => navigate('/features')} className="text-orange-500 text-xs font-medium px-2">FEATURES</button>
            <button onClick={() => setShowShareModal(true)} className="p-1.5 text-slate-600 hover:text-slate-900"><Share2 className="w-5 h-5" /></button>
            <Button onClick={() => navigate('/login')} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs px-3">LOG IN</Button>
          </div>
        </div>
      </nav>

      {/* 1. Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 text-blue-400 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 tracking-wider"
          >
            <Truck className="w-4 h-4" />
            SITE UPDATES & DIARY
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <p className="text-blue-400 text-sm md:text-base font-semibold mb-3">
                Finishing up on site? Quick progress note?
              </p>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
                LOG SITE UPDATES<br />
                <span className="text-blue-500">IN SECONDS</span>
              </h1>

              <p className="text-slate-300 text-sm md:text-base mb-4 leading-relaxed">
                Speak what you did today, snap some photos, and 2Tap builds your site diary. 
                Every update timestamped, organized by job, ready to share with the builder.
              </p>

              <p className="text-slate-400 text-sm border-l-2 border-blue-500 pl-3 mb-8">
                Stop scrolling through photos and texts. Your complete job history, always at your fingertips.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => setShowSetupModal(true)} className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm px-6 py-3 font-bold">
                  REGISTER INTEREST<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="hidden md:block">
              <BeforeAfterMockup />
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="md:hidden mt-10">
            <BeforeAfterMockup />
          </motion.div>
        </div>
      </section>

      {/* 2. Problem Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 text-red-500 text-xs font-bold tracking-widest mb-4">
              <AlertCircle className="w-4 h-4" />
              THE PROBLEM
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
              SITE UPDATES GET LOST<br />
              <span className="text-red-500">IN THE NOISE</span>
            </h2>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              Photos buried in your camera roll. Progress updates scattered across text threads. 
              When the builder asks "what happened on Tuesday?" — you're guessing.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            {[
              { icon: Image, text: 'Photos everywhere' },
              { icon: MessageSquare, text: 'Lost in group chats' },
              { icon: Clock, text: 'No clear timeline' }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white rounded-xl p-4 shadow-sm">
                <item.icon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. What 2Tap Creates */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-blue-500 text-xs font-bold tracking-widest mb-4">
              <CheckCircle2 className="w-4 h-4" />
              WHAT 2TAP CREATES
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              ORGANIZED SITE DIARY<br />
              <span className="text-blue-500">JOB BY JOB</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Notebook, title: 'Daily Updates', desc: 'What you did, when you did it, all recorded', color: 'blue' },
              { icon: Image, title: 'Photos Linked', desc: 'Every photo attached to the right job', color: 'purple' },
              { icon: CalendarDays, title: 'Full Timeline', desc: 'Complete history of every job at a glance', color: 'green' },
              { icon: Send, title: 'Share Ready', desc: 'Send updates to builders instantly', color: 'orange' }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. How It Works */}
      <section className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-blue-500 text-xs font-bold tracking-widest mb-4">
              <Zap className="w-4 h-4" />
              HOW IT WORKS
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              END OF DAY<br />
              <span className="text-blue-500">IN 30 SECONDS</span>
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              { step: '1', title: 'Tap & Talk', desc: 'Open 2Tap and speak your update. What you did, any issues, what\'s next.' },
              { step: '2', title: 'Snap Photos', desc: 'Add progress photos directly from camera or gallery. They\'re automatically linked to this update.' },
              { step: '3', title: 'Save to Diary', desc: 'Hit save. Your update is timestamped and added to the job\'s site diary. Done.' }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-black text-xl">{item.step}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Example Output */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-slate-500 text-xs font-bold tracking-widest mb-4">
              <FileText className="w-4 h-4" />
              EXAMPLE
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              SEE THE<br />
              <span className="text-blue-500">OUTPUT</span>
            </h2>
          </motion.div>

          <ExampleSiteUpdateOutput />
        </div>
      </section>

      {/* 6. Why It Matters */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
              WHY IT MATTERS
            </h2>
            <p className="text-blue-100 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              A complete site diary isn't just good practice — it's your proof when things get questioned.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: 'Protect Yourself', desc: 'Documented proof of work done' },
              { icon: Users, title: 'Keep Builder Happy', desc: 'Regular updates build trust' },
              { icon: Clock, title: 'Save Time', desc: 'No more end-of-job scramble' }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                <item.icon className="w-10 h-10 text-white mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-blue-100">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Where It Fits */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-xs font-bold mb-4">
              <Truck className="w-4 h-4" />
              PART OF DELIVER THE WORK
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              WHERE IT FITS IN<br />
              <span className="text-blue-500">2TAP</span>
            </h2>
          </motion.div>

          <div className="bg-slate-50 rounded-2xl p-6 md:p-8 shadow-sm mb-10">
            <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4">
              <WorkflowStep icon={ClipboardCheck} label="SWMS Signed" isCompleted />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={Truck} label="Site Updates" isActive />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={AlertTriangle} label="Variations" />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={CheckCircle2} label="Job Complete" />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={Receipt} label="Invoice" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {relatedAreas.map((item, i) => (
              <div 
                key={i} 
                onClick={() => navigate(item.href)}
                className={`flex items-center gap-2 rounded-lg p-3 cursor-pointer transition-all hover:scale-[1.02] ${
                  item.active 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                <item.icon className={`w-5 h-5 ${item.active ? 'text-white' : 'text-blue-500'}`} />
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Final CTA */}
      <section className="py-16 md:py-24 px-4 bg-slate-900">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              READY TO BUILD<br />
              <span className="text-blue-500">A BETTER SITE DIARY?</span>
            </h2>
            <p className="text-slate-400 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Builders love tradies who keep them in the loop. Get your business set up with 2Tap and stand out from the competition.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => setShowSetupModal(true)} className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm px-8 py-4 font-bold text-lg">
                REGISTER INTEREST<ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="py-6 px-4 bg-slate-950">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-4">
          <button 
            onClick={() => setShowSetupModal(true)}
            className="text-slate-400 hover:text-orange-400 text-sm transition-colors"
          >
            Want a custom 2Tap for your business or industry? <span className="underline">Let's talk →</span>
          </button>
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">© 2025 2Tap. Powered for Smart Business.</p>
            <div className="flex items-center gap-6">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Home</button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/features'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Features</button>
              <button onClick={() => setShowShareModal(true)} className="text-slate-400 hover:text-white text-sm transition-colors">Share</button>
            </div>
          </div>
        </div>
      </footer>

      <RequestSetupModal isOpen={showSetupModal} onClose={() => setShowSetupModal(false)} />
      <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} />
    </div>
  );
};

export default FeatureSiteUpdates;
